/**
 * What a repository's sandbox reaches under restricted internet access, and
 * which hosts its allowlist can take. The firewall's input
 * (`repositoryNetworkPolicy`), every save of the allowlist
 * (`parseOutboundHosts`), the suggestion card and the first environment's
 * check all read these rules, so they cannot disagree. No Node or server
 * imports: the card runs in the browser.
 */

import type { CodeRepositoryEnvironmentView, CodeRepositoryNetworkSettings } from './types';

import { isCredentialHostName } from './credential-syntax';
import { CODE_PACKAGE_REGISTRY_HOSTS } from './package-registry-hosts';

/**
 * Where GitHub serves Actions job logs from: the API's logs endpoint redirects
 * to one of GitHub's own Azure storage accounts. Auto-fix reads a failed job's
 * full log there when it fixes CI (`gh run view --log-failed`). GitHub lists
 * these 20 accounts under "GitHub: Actions Artifact Storage":
 * https://docs.github.com/en/copilot/reference/copilot-allowlist-reference#github-actions-artifact-storage
 * Allowed in every sandbox, but named to the agent as one entry
 * (`buildCodeSessionNetworkNote`), so they do not crowd out the repository's own hosts.
 */
export const GITHUB_ACTIONS_LOG_HOSTS = Array.from(
  { length: 20 },
  (_, index) => `productionresultssa${index}.blob.core.windows.net`,
);

/**
 * The only host a spare sandbox (`quick-chat/`) reaches beside cubic's own endpoints and its
 * person's MCP servers: it has no repository, so none of GitHub's or the repository's hosts.
 */
export const SPARE_OUTBOUND_HOSTS = ['ai-gateway.vercel.sh'] as const;

export const CODE_SESSION_DEFAULT_OUTBOUND_HOSTS = [
  'github.com',
  'api.github.com',
  // GitHub profile pictures, which apps show, often through `next/image` on the
  // dev server; blocked, they show as broken images in demos.
  'avatars.githubusercontent.com',
  // Where github.com redirects downloads: release assets (as when an npm
  // install script such as inngest-cli's fetches its binary), repository
  // archives (npm's github: dependencies) and raw files.
  'release-assets.githubusercontent.com',
  'objects.githubusercontent.com',
  'codeload.github.com',
  'raw.githubusercontent.com',
  ...GITHUB_ACTIONS_LOG_HOSTS,
  'ai-gateway.vercel.sh',
  // Google Fonts' CSS and font files. `next/font/google` downloads them from the
  // dev server, and when it cannot it silently swaps in a fallback, so apps
  // filmed or checked here look broken with no error. Both only serve fonts.
  'fonts.googleapis.com',
  'fonts.gstatic.com',
];

const RESERVED_HOST_SUFFIX_PATTERN =
  /(?:^|\.)(?:localhost|local|localdomain|internal|home|lan|corp|intranet|arpa|test|invalid|example|onion)$/;

/**
 * A host as every save of the allowlist stores it, trimmed and lowercased, and
 * as a change compares a host it names with the hosts saved, some of which
 * were saved before hosts were lowercased.
 */
export function normalizeOutboundHost(host: string): string {
  return host.trim().toLowerCase();
}

/**
 * Whether a trimmed, lowercased host is an exact public host name, as every
 * save of the allowlist requires.
 */
export function isOutboundHostName(host: string): boolean {
  return isCredentialHostName(host) && !RESERVED_HOST_SUFFIX_PATTERN.test(host);
}

/**
 * The hosts a restricted repository allows besides the defaults: the package
 * registries when it allows them, then its own.
 */
export function repositoryAllowedHosts(
  environment: Pick<
    CodeRepositoryNetworkSettings,
    'allowPackageRegistries' | 'allowedOutboundHosts'
  >,
): string[] {
  return [
    ...(environment.allowPackageRegistries ? CODE_PACKAGE_REGISTRY_HOSTS : []),
    ...environment.allowedOutboundHosts,
  ];
}

type AllowedHostsEnvironment = Pick<
  CodeRepositoryEnvironmentView,
  'allowPackageRegistries' | 'allowedOutboundHosts' | 'credentials'
>;

/**
 * Whether a restricted sandbox on the repository reaches a trimmed, lowercased
 * host: a default, a package registry while the repository allows them, a
 * host it allows, or a host one of its credentials goes to, which the
 * firewall opens for the credential. A session's MCP server hosts are not
 * known here.
 */
export function isOutboundHostAllowed(host: string, environment: AllowedHostsEnvironment): boolean {
  // A host saved before hosts were lowercased keeps its case.
  const isHost = (saved: string) => normalizeOutboundHost(saved) === host;
  return (
    CODE_SESSION_DEFAULT_OUTBOUND_HOSTS.includes(host) ||
    repositoryAllowedHosts(environment).some(isHost) ||
    environment.credentials.some((credential) => credential.hosts.some(isHost))
  );
}
